import { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost } from '../api';
import { resolveTourTrack } from './index';

export const useTourController = (currentUser, token, tenantFeatures, activePage) => {
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [tourKey, setTourKey] = useState(null);
  const [isTourLoading, setIsTourLoading] = useState(true);
  const [tourState, setTourState] = useState({});

  // Guards to prevent the effect from firing more than once per user session
  const hasCheckedRef = useRef(false);
  const pendingTimeoutRef = useRef(null);
  const lastUserIdRef = useRef(null);

  // Stable primitive keys — avoids re-triggering on object reference changes
  const userId = currentUser?.id;
  const roleId = currentUser?.roleId;
  const tenantId = currentUser?.tenantId;

  useEffect(() => {
    // Only run if we have a valid user + token
    if (!userId || !token) {
      setIsTourLoading(false);
      return;
    }

    // Only run once per user login session (not on every tenantFeatures / currentUser re-render)
    if (hasCheckedRef.current && lastUserIdRef.current === userId) {
      console.log(`[TourController] ⏩ Already checked for user ${userId}, skipping re-run`);
      return;
    }

    hasCheckedRef.current = true;
    lastUserIdRef.current = userId;

    // Determine the appropriate tour track and key
    const resolved = resolveTourTrack({ id: userId, roleId, tenantId }, tenantFeatures);
    if (!resolved) {
      console.log(`[TourController] ℹ️  No tour track resolved for role: ${roleId}`);
      setIsTourLoading(false);
      return;
    }

    setSteps(resolved.steps);
    setTourKey(resolved.key);

    const checkTourState = async () => {
      try {
        console.log(`[TourController] 🔍 Checking tour state for user: ${userId} | role: ${roleId} | key: ${resolved.key}`);

        // 1. Try local storage cache for immediate check
        const localCacheKey = `aidas_tour_state_${userId}`;
        const cached = localStorage.getItem(localCacheKey);
        let cachedState = {};

        if (cached) {
          try {
            cachedState = JSON.parse(cached);
            console.log('[TourController] 📦 Local cache hit:', cachedState);
            // If cache already says seen, skip the API call
            if (cachedState[resolved.key] === true) {
              console.log(`[TourController] 🔕 Cache says already seen → skipping auto-start`);
              setTourState(cachedState);
              setIsTourLoading(false);
              return;
            }
          } catch (e) {}
        } else {
          console.log('[TourController] 📭 No local cache found');
        }

        // 2. Fetch true state from backend
        console.log('[TourController] 🌐 Fetching tour state from server...');
        const response = await apiGet('/users/me/tour-state', token);
        const serverState = response || {};
        console.log('[TourController] ✅ Server state received:', serverState);

        // Update state + cache
        setTourState(serverState);
        localStorage.setItem(localCacheKey, JSON.stringify(serverState));

        // 3. Auto-start only if not seen
        if (!serverState[resolved.key]) {
          console.log(`[TourController] 🎬 Tour key "${resolved.key}" NOT seen → auto-starting in 1500ms`);
          // Cancel any previously scheduled timer (safety net)
          if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
          pendingTimeoutRef.current = setTimeout(() => {
            console.log(`[TourController] ▶️  setRun(true) fired`);
            setRun(true);
            pendingTimeoutRef.current = null;
          }, 1500);
        } else {
          console.log(`[TourController] 🔕 Tour key "${resolved.key}" already seen → skipping auto-start`);
        }
      } catch (error) {
        console.error('[TourController] ❌ Failed to fetch tour state:', error);
      } finally {
        setIsTourLoading(false);
      }
    };

    checkTourState();

    // Cleanup pending timeout if component unmounts
    return () => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    };
  }, [userId, roleId, tenantId, token]); // ← stable primitives only, NOT tenantFeatures/currentUser objects

  const markSeen = useCallback(async (keyToMark = tourKey) => {
    if (!keyToMark || !userId || !token) return;
    console.log(`[TourController] 💾 markSeen called → key: "${keyToMark}"`);

    // Optimistic local update
    setRun(false);
    const newState = { ...tourState, [keyToMark]: true };
    setTourState(newState);
    localStorage.setItem(`aidas_tour_state_${userId}`, JSON.stringify(newState));

    // Backend update
    try {
      await apiPost('/users/me/tour-state', token, { [keyToMark]: true });
      console.log(`[TourController] ✅ markSeen saved to server → key: "${keyToMark}"`);
    } catch (error) {
      console.error('[TourController] ❌ Failed to save tour state:', error);
    }
  }, [tourKey, tourState, userId, token]);

  const restartTour = useCallback(async () => {
    if (!userId || !token) return;
    console.log('[TourController] 🔄 restartTour called');

    // Re-resolve using current user shape
    const resolved = resolveTourTrack(currentUser, tenantFeatures);
    if (!resolved) {
      console.warn('[TourController] ⚠️  restartTour: no tour track resolved');
      return;
    }
    console.log(`[TourController] 🎯 Restarting tour → key: "${resolved.key}" | steps: ${resolved.steps.length}`);

    setSteps(resolved.steps);
    setTourKey(resolved.key);

    // Clear seen flag locally
    const newState = { ...tourState, [resolved.key]: false };
    setTourState(newState);
    localStorage.setItem(`aidas_tour_state_${userId}`, JSON.stringify(newState));

    console.log('[TourController] ▶️  setRun(true) from restartTour');
    setRun(true);
  }, [currentUser, tenantFeatures, tourState, userId, token]);

  return { steps, run, setRun, markSeen, restartTour, isTourLoading };
};
